import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronRight, ChevronDown } from "lucide-react";

/**
 * Two-level nested renderer used by MobileMenuDrawer.
 * Split into its own file to avoid Babel's AST-traversal stack
 * overflow that happens when several components reference each
 * other recursively in the same JSX file.
 *
 * Visual hierarchy:
 *   - TOP level (Features / Pricing / Resources / About) → bigger + bolder
 *   - SUB level (children inside a group)                → slightly smaller
 */
const BASE_TOP = "group relative flex items-center gap-4 w-full text-left px-4 py-3.5 rounded-full transition-all duration-200 font-bold text-[17px]";
const BASE_SUB = "group relative flex items-center gap-3.5 w-full text-left px-4 py-3 rounded-full transition-all duration-200 font-semibold text-[15.5px]";
const INACTIVE_TOP = "text-ink hover:bg-plum/5";
const INACTIVE_SUB = "text-inkmid hover:bg-plum/5 hover:text-ink";
const ACTIVE = "bg-plum/10 text-ink font-bold shadow-[inset_0_0_0_1px_rgba(94,68,144,0.05)]";

const ICON_TOP_CLS = "w-6 h-6 shrink-0";
const ICON_SUB_CLS = "w-5 h-5 shrink-0";
const ICON_STROKE = 2.25;

function styleFor(level) {
  return level === "top"
    ? { base: BASE_TOP, inactive: INACTIVE_TOP, icon: ICON_TOP_CLS }
    : { base: BASE_SUB, inactive: INACTIVE_SUB, icon: ICON_SUB_CLS };
}

/** Best-effort match of a drawer item against the current router location. */
function itemMatchesLocation(item, location) {
  if (!item || typeof item.to !== "string") return false;
  try {
    const url = new URL(item.to, "http://_");
    if (url.pathname !== location.pathname) return false;
    if (item.to.includes("#")) return url.hash === location.hash;
    if (item.to.includes("?")) return url.search === location.search;
    return true;
  } catch { return false; }
}

/**
 * If the target `to` includes a `#hash`, scroll the matching section into
 * view immediately when the user is already on that page. React-router's
 * <Link> is a no-op for identical destinations (e.g. clicking Pricing twice
 * while already at /#plans), so without this the second tap "does nothing".
 * When the section isn't mounted yet, we no-op here and let ScrollToTop's
 * post-navigation hash handler pick it up.
 */
function jumpToHash(to) {
  if (typeof to !== "string") return;
  const i = to.indexOf("#");
  if (i < 0) return;
  const id = to.slice(i + 1);
  if (!id) return;
  const el = document.getElementById(id);
  if (!el) return;
  try { el.scrollIntoView({ behavior: "auto", block: "start" }); }
  catch {
    const top = el.getBoundingClientRect().top + (window.scrollY || 0);
    window.scrollTo(0, top);
  }
}

export function TopItem({ item, onClose }) {
  if (Array.isArray(item.children) && item.children.length) {
    return <TopGroup item={item} onClose={onClose} />;
  }
  return <Leaf item={item} onClose={onClose} level="top" />;
}

function TopGroup({ item, onClose }) {
  // Auto-expand if any descendant matches the current route — keeps the
  // selected section visible when the drawer opens.
  const location = useLocation();
  const childIsActive = (item.children || []).some((c) => {
    if (Array.isArray(c.children) && c.children.length) {
      return c.children.some((sc) => itemMatchesLocation(sc, location));
    }
    return itemMatchesLocation(c, location);
  });
  const [open, setOpen] = useState(!!item.defaultOpen || childIsActive);
  const Icon = item.icon;
  const slug = String(item.label || "").toLowerCase().replace(/\s+/g, "-");
  // Prefer caller-provided testid so pages that mix collapsible groups with
  // top-level leaf items can address them uniformly.
  const groupTestId = item.testid || ("drawer-group-" + slug);
  const s = styleFor("top");
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={groupTestId}
        className={s.base + " " + (open ? "bg-coral/10 text-coral" : s.inactive)}
      >
        {Icon && <Icon className={s.icon} strokeWidth={ICON_STROKE} />}
        <span className="flex-1 truncate">
          {item.label}
          {item.badge && (
            <span
              className="kp-badge-new ml-1.5"
              aria-label="New"
              data-testid={`${groupTestId}-badge`}
            >
              {item.badge}
            </span>
          )}
        </span>
        <ChevronDown className={"w-5 h-5 shrink-0 transition-transform " + (open ? "rotate-180" : "")} strokeWidth={2.4} />
      </button>
      {open && (
        <ul
          className="mt-1 mb-1 space-y-0.5 border-l-2 border-coral/15 ml-5"
          data-testid={groupTestId + "-children"}
        >
          {item.children.map((child, i) => (
            <li key={(child && (child.to || child.href || child.label)) || i} className="pl-2">
              {Array.isArray(child.children) && child.children.length
                ? <SubGroup item={child} onClose={onClose} />
                : <Leaf item={child} onClose={onClose} level="sub" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SubGroup({ item, onClose }) {
  const [open, setOpen] = useState(false);
  const Icon = item.icon;
  const slug = String(item.label || "").toLowerCase().replace(/\s+/g, "-");
  const groupTestId = item.testid || ("drawer-group-" + slug);
  const s = styleFor("sub");
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={groupTestId}
        className={s.base + " " + (open ? "bg-coral/10 text-coral" : s.inactive)}
      >
        {Icon && <Icon className={s.icon} strokeWidth={ICON_STROKE} />}
        <span className="flex-1 truncate">{item.label}</span>
        <ChevronDown className={"w-4 h-4 shrink-0 transition-transform " + (open ? "rotate-180" : "")} strokeWidth={2.4} />
      </button>
      {open && (
        <ul
          className="mt-1 mb-1 space-y-0.5 border-l-2 border-coral/15 ml-4"
          data-testid={groupTestId + "-children"}
        >
          {(item.children || []).map((leaf, i) => (
            <li key={(leaf && (leaf.to || leaf.href || leaf.label)) || i} className="pl-2">
              <Leaf item={leaf} onClose={onClose} level="sub" />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Leaf({ item, onClose, level = "top" }) {
  const Icon = item.icon;
  const s = styleFor(level);
  const chevronCls = level === "top" ? "w-5 h-5" : "w-4 h-4";
  const location = useLocation();

  if (item.external || (item.to && /^(https?:|mailto:|tel:)/.test(item.to))) {
    const href = item.to || item.href;
    return (
      <a
        href={href}
        target={/^https?:/.test(href || "") ? "_blank" : undefined}
        rel="noopener noreferrer"
        onClick={onClose}
        className={s.base + " " + s.inactive}
        data-testid={item.testid}
      >
        {Icon && <Icon className={s.icon} strokeWidth={ICON_STROKE} />}
        <span className="flex-1 truncate">{item.label}</span>
      </a>
    );
  }
  if (!item.to && item.onClick) {
    return (
      <button type="button" onClick={() => { item.onClick(); onClose(); }}
        className={s.base + " " + s.inactive} data-testid={item.testid}>
        {Icon && <Icon className={s.icon} strokeWidth={ICON_STROKE} />}
        <span className="flex-1 truncate">{item.label}</span>
      </button>
    );
  }
  // For items pointing at a path with a query string (e.g. /resources?cat=books)
  // or a hash anchor (e.g. /#how) NavLink wouldn't compute the right active
  // state — pathname-only match would light up every sibling on the base
  // path. Use plain Link and compute the active state ourselves: same
  // pathname AND (same hash OR same search). This makes the currently-open
  // section in the drawer visibly selected.
  const hasQuery = typeof item.to === "string" && item.to.includes("?");
  const hasHash = typeof item.to === "string" && item.to.includes("#");
  if (hasQuery || hasHash) {
    let isActiveHashOrQuery = false;
    try {
      const url = new URL(item.to, "http://_");
      const samePath = url.pathname === location.pathname;
      if (samePath && hasHash) isActiveHashOrQuery = url.hash === location.hash;
      if (samePath && hasQuery) isActiveHashOrQuery = url.search === location.search;
    } catch { /* noop */ }
    return (
      <Link
        to={item.to}
        onClick={() => { if (hasHash) jumpToHash(item.to); onClose(); }}
        data-testid={item.testid}
        className={s.base + " " + (isActiveHashOrQuery ? ACTIVE : s.inactive)}
      >
        {Icon && <Icon className={s.icon} strokeWidth={ICON_STROKE} />}
        <span className="flex-1 truncate">{item.label}</span>
        <ChevronRight className={chevronCls + " text-ink/30 ml-1"} strokeWidth={2.4} />
      </Link>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end !== undefined ? item.end : true}
      onClick={onClose}
      data-testid={item.testid}
      className={({ isActive }) => s.base + " " + (isActive ? ACTIVE : s.inactive)}
    >
      {Icon && <Icon className={s.icon} strokeWidth={ICON_STROKE} />}
      <span className="flex-1 truncate">{item.label}</span>
      <ChevronRight className={chevronCls + " text-ink/60 ml-1"} strokeWidth={2.4} />
    </NavLink>
  );
}
